'use strict'

import React, { useContext, useEffect, useState } from 'react'

import { accessibilityLabel } from '@components/kit/_support'
import Box from '@components/kit/box'
import Text from '@components/kit/text'
import Loading from '@components/kit/loading'
import Tabs, { Tab } from '@components/kit/tabs'

import { ChatContext } from '../chat.web'
import { useMemberSavingsStore } from '@store/MemberSavings'
import {
  IMemberSaving,
  MemberSavingsCategory
} from '@store/MemberSavings/types'
import MemberSavingsItem from './member-savings-item'

const MEMBER_SAVINGS_TABS: {
  key: MemberSavingsCategory
  label: string
}[] = [
  { key: 'cost_savings', label: 'Cost Savings' },
  { key: 'clinical_impact', label: 'Clinical Impact' }
]

function MemberSavings() {
  const { userId } = useContext(ChatContext)
  const [activeTab, setActiveTab] =
    useState<MemberSavingsCategory>('cost_savings')
  const [items, setItems] = useState<IMemberSaving[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { getMemberSavings } = useMemberSavingsStore.getState()

  useEffect(() => {
    let cancelled = false

    const fetchItems = async () => {
      setIsLoading(true)
      const results = await getMemberSavings(userId, activeTab)
      if (!cancelled) {
        setItems(results)
        setIsLoading(false)
      }
    }

    fetchItems()

    return () => {
      cancelled = true
    }
  }, [userId, activeTab, getMemberSavings])

  const handleUpdated = (updatedItem: IMemberSaving) => {
    setItems(prev =>
      prev.map(item => (item.id === updatedItem.id ? updatedItem : item))
    )
  }

  const handleRejected = (savingId: number) => {
    setItems(prev => prev.filter(item => item.id !== savingId))
  }

  return (
    <Box pt='24' {...accessibilityLabel('MemberSavings')}>
      <Text ml='12' mb='08' font='display.16.bold'>
        Member Savings
      </Text>

      <Box mt='04' mb='12' position='relative'>
        <Box
          flex='1'
          height='2px'
          bg='shade04b'
          position='absolute'
          bottom='0'
          left='0'
          right='0'
        />
        <Tabs>
          {MEMBER_SAVINGS_TABS.map(tab => (
            <Tab
              key={tab.key}
              bg='transparent'
              onPress={() => setActiveTab(tab.key)}
              active={activeTab === tab.key}
            >
              {tab.label}
            </Tab>
          ))}
        </Tabs>
      </Box>

      {isLoading && <Loading mt='12' py='12' />}

      {!isLoading && items.length === 0 && (
        <Box padding='16' alignItems='center'>
          <Text font='text.14.medium' textColor='shade05'>
            No pending items available for review.
          </Text>
        </Box>
      )}

      {!isLoading && items.length > 0 && (
        <Box gap='12'>
          {items.map((item, index) => (
            <MemberSavingsItem
              key={item.id}
              index={index + 1}
              item={item}
              userId={userId}
              category={activeTab}
              onUpdated={handleUpdated}
              onRejected={handleRejected}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default MemberSavings
